/**
 * Pure CPF inline/order edit-state helpers.
 * Keep cell remount / save / cancel bugs testable without AG Grid.
 */
import moment from 'moment-timezone';
import { findMinEditableDate } from '@mummssoftware/utils/frequencies/frequency-utils';
import {
  Daily,
  Refused,
  ZeroFreq,
  Discontinued,
} from '@mummssoftware/utils/frequencies/frequency-constants';

type FrequencySeedPlan = {
  editPeriod?: string | null;
  editFrequency?: number | string | null;
  period?: string;
  frequency?: number | string | null;
  frequencyText?: string;
};

type DaysSeedPlan = {
  editDaysOfWeek?: string | null;
  daysOfWeek?: string;
};

type EffectiveSeedPlan = {
  editEffective?: Date | string | null;
  effectiveDate?: string;
};

export type InlineFrequencySeed = {
  period: string | null;
  frequency: number;
};

const toFrequencyNumber = (
  value: number | string | null | undefined,
  fallback = 1,
) => (value != null ? Number(value) : fallback);

const frequencyFromPlanText = (carePlan: FrequencySeedPlan) => {
  if (
    carePlan.frequencyText === Refused ||
    carePlan.frequencyText === ZeroFreq ||
    carePlan.frequencyText === Discontinued
  ) {
    return 0;
  }
  return toFrequencyNumber(carePlan.frequency);
};

/**
 * Priority: Way 1 staged entry → in-progress edit* → saved plan.
 * Blank order entry (staged, no period) → period null (do not seed from edit*).
 */
export const resolveInlineFrequencySeed = ({
  isOrderMode,
  stagedEntry,
  carePlan,
}: {
  isOrderMode: boolean;
  stagedEntry?: { period?: string; frequency?: number | null } | null;
  carePlan: FrequencySeedPlan;
}): InlineFrequencySeed => {
  if (isOrderMode && stagedEntry && !stagedEntry.period) {
    return {
      period: null,
      frequency: toFrequencyNumber(
        carePlan.editFrequency != null
          ? carePlan.editFrequency
          : carePlan.frequency,
      ),
    };
  }

  if (isOrderMode && stagedEntry?.period) {
    return {
      period: stagedEntry.period,
      frequency: toFrequencyNumber(
        stagedEntry.frequency != null
          ? stagedEntry.frequency
          : carePlan.frequency,
      ),
    };
  }

  if (carePlan.editPeriod != null && carePlan.editFrequency != null) {
    return {
      period: String(carePlan.editPeriod),
      frequency: Number(carePlan.editFrequency),
    };
  }

  if (carePlan.period) {
    return {
      period: String(carePlan.period),
      frequency: frequencyFromPlanText(carePlan),
    };
  }

  return { period: Daily, frequency: 1 };
};

/**
 * Non-staged days seed for Way 2 remount.
 * Prefer editDaysOfWeek (including '') over saved daysOfWeek.
 */
export const resolveInlineDaysSeed = (
  carePlan?: DaysSeedPlan | null,
): { localDays: string[]; nextEditDaysOfWeek: string } => {
  if (!carePlan) {
    return { localDays: [], nextEditDaysOfWeek: '' };
  }
  if (carePlan.editDaysOfWeek != null) {
    return {
      localDays: carePlan.editDaysOfWeek.split(',').filter(Boolean),
      nextEditDaysOfWeek: carePlan.editDaysOfWeek,
    };
  }
  if (carePlan.daysOfWeek && typeof carePlan.daysOfWeek === 'string') {
    return {
      localDays: carePlan.daysOfWeek.split(',').filter(Boolean),
      nextEditDaysOfWeek: carePlan.daysOfWeek,
    };
  }
  return { localDays: [], nextEditDaysOfWeek: '' };
};

/** Prefer in-progress editEffective so failed-save remount keeps the calendar. */
export const resolveInlineEffectiveSeed = (
  carePlan?: EffectiveSeedPlan | null,
): Date | null => {
  if (!carePlan) return null;
  if (carePlan.editEffective != null) {
    return carePlan.editEffective instanceof Date
      ? carePlan.editEffective
      : new Date(carePlan.editEffective);
  }
  if (carePlan.effectiveDate) {
    return new Date(carePlan.effectiveDate);
  }
  return null;
};

type MinEffectivePlan = {
  effectiveDate?: string;
  nonCurrentRows?: { effectiveDate?: string }[] | null;
};

/**
 * Calendar floor for inline Effective edits of an existing row.
 * Never floor on the row's own effectiveDate — that locks a mis-entered
 * future date in place. Floor on the latest older (history) frequency instead.
 */
export const resolveInlineMinEffectiveDate = (
  basicDateConstraint: Date | undefined,
  carePlan?: MinEffectivePlan | null,
): string | null => {
  const own = carePlan?.effectiveDate
    ? moment(new Date(carePlan.effectiveDate))
    : null;
  const priorDates = (carePlan?.nonCurrentRows || [])
    .map((row) =>
      row?.effectiveDate ? moment(new Date(row.effectiveDate)) : null,
    )
    .filter(
      (d): d is moment.Moment =>
        !!d && d.isValid() && (!own || d.isBefore(own, 'day')),
    );
  const latestPrior = priorDates.length ? moment.max(priorDates) : null;
  return findMinEditableDate(
    basicDateConstraint,
    latestPrior ? latestPrior.format('MM/DD/YY') : undefined,
  );
};

/**
 * Fill missing edit* from saved plan before Way 2 postCarePlan.
 * Returns false when required fields are still missing.
 * editDaysOfWeek '' is intentional (Daily / cleared) — only refill when nullish.
 */
export const prepareInlineSaveEdits = (
  carePlan: mumms.ProcessedCarePlan,
): boolean => {
  if (!carePlan.editPeriod && carePlan.period) {
    carePlan.editPeriod = carePlan.period;
  }
  if (carePlan.editFrequency == null && carePlan.frequency != null) {
    carePlan.editFrequency = carePlan.frequency;
  }
  if (!carePlan.editEffective && carePlan.effectiveDate) {
    carePlan.editEffective = new Date(carePlan.effectiveDate);
  }
  if (carePlan.editDaysOfWeek == null) {
    carePlan.editDaysOfWeek = carePlan.daysOfWeek || '';
  }
  return !!(
    carePlan.editPeriod &&
    carePlan.editFrequency != null &&
    carePlan.editEffective
  );
};

/**
 * Way 1 CPF order submit shape: staged VForderData entry → edit* plan
 * (mirrors OrdersGlobalContext handleCPFCreation CPF branch).
 */
export const mapCpfOrderEntryToEditPlan = (cpvf: {
  id: number | string;
  sourcePlanId?: number | string | null;
  sourceEffectiveDate?: string | Date | null;
  period?: string;
  frequency?: number | string;
  daysOfWeek?: string;
  effectiveDate?: string | Date;
  prn?: boolean | null;
}) => ({
  id: cpvf.sourcePlanId,
  effectiveDate: cpvf.sourceEffectiveDate || cpvf.effectiveDate,
  editPeriod: cpvf.period,
  editFrequency: cpvf.frequency,
  editEffective: cpvf.effectiveDate,
  editPrn: cpvf.prn,
  editing: false,
  roleGroupId: cpvf.id,
  editDaysOfWeek: cpvf.daysOfWeek,
});
